import { useMutation } from "@tanstack/react-query";
import {
  AssessmentQuestionCountDto,
  UserProfileDto,
  UserRole,
} from "@green0meter/api-client";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import { DOMAIN_DOUBLE_MATERIALITY } from "../domain";

import { roleGuard } from "@api";
import { doubleMaterialityAssessmentsApi } from "@api/services";

const ACTION_GET_QUESTION_COUNT = "GET_ASSESSMENT_QUESTION_COUNT";

const getAssessmentQuestionCount = async (
  id: string,
): Promise<AssessmentQuestionCountDto> => {
  const response =
    await doubleMaterialityAssessmentsApi.doubleMaterialityAssessmentsControllerGetAssessmentQuestionCount(
      id,
    );

  return response.data;
};

export const useGetAssessmentQuestionCount = (
  user: UserProfileDto | null,
): {
  getAssessmentQuestionCountAsync: (
    id: string,
  ) => Promise<AssessmentQuestionCountDto>;
  isPendingAssessmentQuestionCount: boolean;
  isSuccessAssessmentQuestionCount: boolean;
  isErrorAssessmentQuestionCount: boolean;
  errorAssessmentQuestionCount: Error | null;
  enabled: boolean;
} => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const enabled = roleGuard(user, [UserRole.Owner, UserRole.Manager]);

  const mutation = useMutation({
    mutationKey: [DOMAIN_DOUBLE_MATERIALITY, ACTION_GET_QUESTION_COUNT],
    mutationFn: getAssessmentQuestionCount,
    onError: (error: Error) => {
      enqueueSnackbar(
        error.message ||
          t(
            "assessments.messages.questionCountError",
            "Unable to load linked questions.",
          ),
        { variant: "error" },
      );
    },
  });

  return {
    getAssessmentQuestionCountAsync: mutation.mutateAsync,
    isPendingAssessmentQuestionCount: mutation.isPending,
    isSuccessAssessmentQuestionCount: mutation.isSuccess,
    isErrorAssessmentQuestionCount: mutation.isError,
    errorAssessmentQuestionCount: mutation.error,
    enabled,
  };
};
